import { DateTime } from 'luxon';
import type {
  IAnalyticsTag,
  IWorkerTimesheetEntry,
  IWorkerTimesheetReport,
} from '@apis/workerTimesheet';
import type { IGroupedOptions, ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import { professionColor } from '@utils/professionColor';
import {
  NO_TRADE_ID,
  type IGroupedView,
  type IKpis,
  type ITagTimesheetView,
  type ITradeBucket,
} from './types';

const HOURS_PER_MS = 1 / (60 * 60 * 1000);

/** Round to 2 decimal places (avoids the long-tail float artifacts). */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Compute per-tag KPIs from a list of timesheet entries.
 *
 *   - `workingDaysCount`: unique `dateIso` values amongst entries with a
 *     non-null `exitIso`. An open shift (worker hasn't checked out yet)
 *     is excluded so the count reflects *completed* working days.
 *   - `totalWorkingHours`: sum of `(exit - arrival)` durations in hours,
 *     rounded to two decimals. Same exclusion as above.
 *   - `avgDailyHours`: `totalWorkingHours / workingDaysCount`, 2 dp.
 *     Returns 0 when the denominator is 0 (rather than NaN) so the UI
 *     can render a stable zero state.
 */
export function computeKpis(entries: IWorkerTimesheetEntry[]): IKpis {
  let totalMs = 0;
  const workingDays = new Set<string>();
  for (const entry of entries) {
    if (entry.exitIso === null) continue;
    workingDays.add(entry.dateIso);
    const start = DateTime.fromISO(entry.arrivalIso);
    const end = DateTime.fromISO(entry.exitIso);
    if (start.isValid && end.isValid) {
      totalMs += Math.max(0, end.toMillis() - start.toMillis());
    }
  }
  const workingDaysCount = workingDays.size;
  const totalWorkingHours = round2(totalMs * HOURS_PER_MS);
  const avgDailyHours =
    workingDaysCount === 0 ? 0 : round2(totalWorkingHours / workingDaysCount);
  return { avgDailyHours, workingDaysCount, totalWorkingHours };
}

/**
 * Empty-safe baseline for the grouped view — used when the report is
 * still loading or undefined. Keeps the consuming components free of
 * defensive null checks.
 */
const EMPTY_GROUPED_VIEW: IGroupedView = {
  byTag: {},
  groupedByKind: { worker: {}, manager: {} },
  tagOrder: [],
};

/**
 * Build the grouped view consumed by the Worker Timesheet report page.
 *
 *   - Preserves the backend's `byTag` order in `tagOrder` (the backend
 *     guarantees insertion order matches the request's tagIds list).
 *   - `groupedByKind.worker` and `groupedByKind.manager` are indexed by
 *     the tag's `profession.tradeName ?? tradeId` (the friendly label),
 *     with a synthetic `__no-trade__` bucket for tags whose profession
 *     is null (typical for managers). Grouping by display name keeps
 *     the sidebar headers human-readable; the rare case where upstream
 *     omits `tradeName` falls back to the opaque `tradeId` so the
 *     section still has a stable key.
 *   - Per-tag KPIs are computed via `computeKpis`.
 *
 * `tz` is reserved for future tz-aware aggregations (e.g. weekly summaries
 * pinned to the project's local week); KPIs themselves use absolute
 * arrival/exit instants and don't require it. Pass the project tz at the
 * call site so the dependency stays visible when those aggregations land.
 */
export function buildGroupedView(
  report: IWorkerTimesheetReport | undefined,
): IGroupedView {
  if (!report) return EMPTY_GROUPED_VIEW;
  const tagOrder = Object.keys(report.byTag);
  const byTag: Record<string, ITagTimesheetView> = {};
  const groupedByKind: Record<'worker' | 'manager', ITradeBucket> = {
    worker: {},
    manager: {},
  };
  for (const tagId of tagOrder) {
    const entry = report.byTag[tagId]!;
    const view: ITagTimesheetView = {
      tagId,
      tag: entry.tag,
      entries: entry.entries,
      absenceDates: entry.absenceDates,
      kpis: computeKpis(entry.entries),
    };
    byTag[tagId] = view;
    // Group by the friendly display label so the sidebar headers read
    // as e.g. "Stitch Works" instead of the opaque `finishing-stitch`
    // tradeId. Falls back to the tradeId when upstream omits the name,
    // and to the synthetic key for tags with no profession at all.
    const groupKey =
      entry.tag.profession?.tradeName ??
      entry.tag.profession?.tradeId ??
      NO_TRADE_ID;
    const bucket = groupedByKind[entry.tag.kind];
    if (!bucket[groupKey]) bucket[groupKey] = [];
    bucket[groupKey].push(view);
  }
  return { byTag, groupedByKind, tagOrder };
}

/**
 * Convert a CamelCase / kebab-case / snake_case tradeGroup into the all-caps
 * display label Trusstor uses for the outer picker section header
 * (e.g. `finishing-stitch` → "FINISHING STITCH", `mep` → "MEP"). Returns
 * null for empty / missing input.
 */
function tradeGroupToCapsLabel(tradeGroup: string | null | undefined): string | null {
  if (!tradeGroup) return null;
  const normalized = tradeGroup.replace(/[-_]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return normalized.toUpperCase();
}

const MANAGER_TOP_LEVEL_ID = 'managers';
const MANAGER_TOP_LEVEL_LABEL = 'MANAGERS';

/**
 * Convert the raw tags list into `IGroupedOptions[]` for the card's
 * `MultiSelect`. Mirrors Trusstor's two-level
 * `MultiTagsGroupedByTradeDropdown` structure:
 *   - **Top-level** = `tradeGroup` (caps, e.g. "SYSTEMS"). Workers without
 *     a tradeGroup fall under a synthetic "OTHERS" bucket; managers go
 *     under "MANAGERS".
 *   - **Inner** = `tradeName` per profession (e.g. "Stitch Works").
 *   - **Row label** = `${tagNick} - ${fullName}` (collapsed when both
 *     fields carry the same short value, e.g. simulation data).
 */
export function tagsToGroupedOptions(tags: IAnalyticsTag[]): IGroupedOptions[] {
  if (tags.length === 0) return [];
  // Use the shared `IGroupedOptions` shape directly (with required
  // top-level fields locally so the builder can fill them unconditionally).
  // Avoids the previous handwritten 7-field duplicate.
  type GroupAcc = Omit<
    IGroupedOptions,
    'topLevelGroupId' | 'topLevelGroupLabel' | 'options' | 'isCollapsible'
  > & {
    topLevelGroupId: string;
    topLevelGroupLabel: string;
    isCollapsible: boolean;
    options: ISelectionOption[];
  };
  const groups = new Map<string, GroupAcc>();
  for (const tag of tags) {
    const isManager = tag.kind === 'manager';
    const tradeKey = tag.profession?.tradeId ?? NO_TRADE_ID;
    const groupId = `${isManager ? 'manager' : 'worker'}:${tradeKey}`;
    // Inner group label (e.g. "Stitch Works"). Falls back to contractor /
    // tradeId for variants that omit a friendly name.
    const groupLabel = isManager
      ? 'Managers'
      : (tag.profession?.tradeName ??
        tag.profession?.contractor ??
        tag.profession?.tradeId ??
        tradeKey);
    // Outer top-level group (e.g. "FINISHING-STITCH"). Managers share a
    // single "MANAGERS" caps header; workers without a tradeGroup fall
    // into "OTHERS".
    const topLevelGroupId = isManager
      ? MANAGER_TOP_LEVEL_ID
      : `tradeGroup:${tag.profession?.tradeGroup ?? 'others'}`;
    const topLevelGroupLabel = isManager
      ? MANAGER_TOP_LEVEL_LABEL
      : (tradeGroupToCapsLabel(tag.profession?.tradeGroup) ?? 'OTHERS');
    let group = groups.get(groupId);
    if (!group) {
      // Chip colour: upstream brand colour if present, otherwise the neutral
      // light-gray fallback (managers never carry a profession colour, so they
      // always take the fallback). Resolved centrally so every surface agrees.
      const groupColor = isManager
        ? professionColor(null)
        : professionColor(tag.profession?.backgroundColor);
      group = {
        groupId,
        groupLabel,
        topLevelGroupId,
        topLevelGroupLabel,
        groupColor,
        isCollapsible: true,
        options: [],
        ...(!isManager && tag.profession?.tradeId
          ? { groupTradeId: tag.profession.tradeId }
          : {}),
      };
      groups.set(groupId, group);
    }
    // Row label: `${tagNick} - ${fullName}` per Trusstor; collapsed when
    // nick === fullName so simulation tags don't render "8 - 8" rows.
    const displayNick = tag.tagNick && tag.tagNick.length > 0 ? tag.tagNick : null;
    const baseLabel =
      displayNick && displayNick !== tag.fullName
        ? `${displayNick} - ${tag.fullName}`
        : (displayNick ?? tag.fullName);
    const label =
      tag.idNumber && tag.idNumber.length > 0
        ? `${baseLabel} (${tag.idNumber})`
        : baseLabel;
    group.options.push({ id: tag.tagId, label });
  }
  // Sort: top-level alphabetical (so "ELECTRICAL" < "FINISHING-STITCH" <
  // "MANAGERS"), inner alphabetical within each top-level bucket. Stable
  // ordering keeps the picker predictable across re-renders.
  const sorted = [...groups.values()].sort((a, b) => {
    const top = a.topLevelGroupLabel.localeCompare(b.topLevelGroupLabel);
    if (top !== 0) return top;
    return a.groupLabel.localeCompare(b.groupLabel);
  });
  return sorted;
}
